import React, { useMemo } from 'react'
import { Fade, IconButton, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material'
import UserAvatar from '@tabeeb/shared/userAvatar'
import { MoreVertOutlined } from '@mui/icons-material'
import { useSelector } from '@tabeeb/store'
import { getQualificationsForInvitesEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { useContentUserQualifications } from '@tabeeb/modules/invites/hooks'
import { getContentId } from '@tabeeb/shared/content/selectors'
import useContentUserQualificationsRender from '@tabeeb/modules/contentUsers/hooks/useContentUserQualificationsRender'
import { ContentUserRole } from '@tabeeb/contracts/main-api/swagger-main-api'
import useUserOptions from '../../../../users/components/UserOptions/useUserOptions'
import CurrentUserOptionsMenu from '../../../../users/components/CurrentUserOptionsMenu'
import UserOptionsMenu from '../../../../users/components/UserOptionsMenu'
import { PluginUser } from '../../../../users/types'

interface ParticipantItemProps {
  user: PluginUser
}

const ParticipantItem: React.FC<ParticipantItemProps> = ({ user }) => {
  const qualificationForInvitesEnabled = useSelector(getQualificationsForInvitesEnabled)
  const contentId = useSelector<number>(getContentId)

  const {
    data: userQualifications,
    isLoading: areQualificationsLoading,
    isError: qualificationsFailedToLoad,
  } = useContentUserQualifications({
    userId: user.identityGuid,
    contentId,
    qualificationIds: user.requiredQualificationIds || [],
    enabled: qualificationForInvitesEnabled,
  })

  const {
    enabled,
    isCertificatesMenuVisible,
    isDeviceMenuVisible,
    isHealthInfoOptionsVisible,
    isCurrentUserOptionsVisible,
    isVisible,
    buttonRef,
    open,
    onOpen,
    onClose,
  } = useUserOptions(user)

  const qualificationsComponent = useContentUserQualificationsRender({
    qualificationsFailedToLoad,
    userQualifications,
    areQualificationsLoading,
    qualificationForInvitesEnabled,
  })

  const shouldRenderQualifications = useMemo(() => {
    const hideForRoles = [ContentUserRole.Owner, ContentUserRole.Reviewer]
    return !hideForRoles.includes(user.role)
  }, [user.role])

  return (
    <Fade in>
      <ListItem disableGutters>
        <ListItemAvatar>
          <UserAvatar user={user} style={undefined} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography
              sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              noWrap
            >
              {user.displayName} {user.isOwner && '(Owner)'} {user.role === ContentUserRole.Reviewer && '(Reviewer)'}
            </Typography>
          }
          secondary={shouldRenderQualifications ? qualificationsComponent : null}
        />
        {enabled ? (
          <>
            <IconButton ref={buttonRef} size='small' onClick={onOpen}>
              <MoreVertOutlined />
            </IconButton>
            {isCurrentUserOptionsVisible && (
              <CurrentUserOptionsMenu
                isOpen={open}
                anchorEl={buttonRef.current}
                onClose={onClose}
                isDeviceMenuVisible={isDeviceMenuVisible}
                isHealthInfoOptionsVisible={isHealthInfoOptionsVisible}
                isCertificatesMenuVisible={isCertificatesMenuVisible}
              />
            )}
            {isVisible && <UserOptionsMenu isOpen={open} anchorEl={buttonRef.current} onClose={onClose} user={user} />}
          </>
        ) : null}
      </ListItem>
    </Fade>
  )
}

export default ParticipantItem
